import List from 'components/List'
import { STYLES_PADDING } from 'modules/styles/constants/index.cjs'
import PropTypes from 'prop-types'
import React from 'react'
import { useSelector } from 'react-redux'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import { isVeryLargeScreenViewportSelect } from 'modules/viewport/selectors/index.js'
import getPathnameRouterSelect from 'modules/router/selectors/get-pathname/index.js'

/**
 * @param children
 * @param style
 * @param direction
 * @param onLoadMoreItems
 * @param shouldScroll
 * @param isBoundToWindow
 * @param shouldNotWrap
 * @param itemCountPerRow
 * @returns {JSX.Element}
 * @constructor
 */
const SectionItems = ({
  children,
  style = {},
  direction = List.DIRECTION.COLUMN,
  onLoadMoreItems,
  shouldScroll,
  isBoundToWindow,
  shouldNotWrap,
  itemCountPerRow
}) => {
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  const isVeryLargeScreen = useSelector(isVeryLargeScreenViewportSelect)
  const pathname = useSelector(getPathnameRouterSelect)

  const childrenCount = React.Children.count(children)
  const isRow = direction === List.DIRECTION.ROW

  let itemsPerRow = itemCountPerRow
  if (!itemsPerRow && isRow && !shouldScroll && isSmallScreen) {
    itemsPerRow = 2
  } else if (!itemsPerRow && isRow && !shouldScroll && isVeryLargeScreen) {
    itemsPerRow = 5
  } else if (!itemsPerRow && isRow && !shouldScroll) {
    itemsPerRow = 4
  }

  const getItemStyle = (index) => {
    let isLast = false
    if (isRow && index && !shouldScroll) {
      isLast = ((index + 1) % itemsPerRow) === 0
    } else if (index && shouldScroll) {
      isLast = index === childrenCount - 1
    }

    const flexBasisCalc = itemsPerRow
      ? `calc(calc(100% / ${itemsPerRow}) + calc(${STYLES_PADDING.BIG} / ${itemsPerRow}) - ${STYLES_PADDING.BIG})`
      : null

    let paddingBottom = STYLES_PADDING.BIG
    if (isLast && !isRow) {
      paddingBottom = null
    }

    let paddingRight = null
    if (isRow && (!isLast || shouldScroll)) {
      paddingRight = STYLES_PADDING.BIG
    }

    let paddingLeft = null
    if (isRow && shouldScroll && !index) {
      paddingLeft = '1.5rem'
    }

    return {
      paddingBottom,
      paddingRight,
      paddingLeft,
      minWidth: flexBasisCalc,
      flexBasis: flexBasisCalc,
      maxWidth: flexBasisCalc
    }
  }

  return (
    <div style={style}>
      <List
        onLoadMoreItems={onLoadMoreItems}
        pathname={pathname}
        direction={direction}
        isBoundToWindow={isBoundToWindow}
        shouldNotWrap={shouldNotWrap}
        isHorizontal={shouldScroll}
        shouldScroll={shouldScroll}
      >
        {React.Children.map(children, (child, index) => (
          <article
            key={index}
            style={getItemStyle(index)}
          >
            {child}
          </article>
        ))}
      </List>
    </div>
  )
}

SectionItems.DIRECTION = List.DIRECTION

SectionItems.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  style: PropTypes.object,
  /**
   * The current pathname.
   * Used to re-init the <Scroll> variables when changing location.
   * @type {Function}
   */
  pathname: PropTypes.string,
  /**
   * @see <List>
   */
  direction: List.propTypes.direction,
  /**
   * Callback used to load items.
   * Used for infinite scroll.
   * @type {Function}
   */
  onLoadMoreItems: PropTypes.func,
  shouldScroll: PropTypes.bool,
  isBoundToWindow: PropTypes.bool,
  shouldNotWrap: PropTypes.bool
}

export default SectionItems
